import { useCallback, useMemo, useState } from 'react';
import { format, isToday, isTomorrow, isThisWeek, parseISO } from 'date-fns';
import { Calendar as CalendarIcon } from 'lucide-react';
import type { CustomField, Task } from '@/lib/schemas';
import type { SprintDef } from '@/hooks/use-calendar';
import { useI18n } from '@/lib/i18n';
import { CalendarContextMenu, type CalendarContextMenuItem } from './calendar-context-menu';
import { colorToHex, resolveCalendarColor, resolveTaskDate, extractIsoDate, type CalendarDateSourceWithAll } from './calendar-config';
import { cn } from '@/lib/utils';

// ============================================
// AGENDA VIEW
// ============================================
//
// t16: list of upcoming tasks sorted by their date (closest first).
// Used as the alternative to Month / Week in the view-mode selector —
// the most "spreadsheet-like" of the three layouts and the easiest
// way to scan what's coming up next.

export interface CalendarAgendaProps {
    anchor: Date;
    tasks: Task[];
    customFields: CustomField[];
    sprints: SprintDef[];
    sprintField: CustomField | null;
    /** T21 R4: user-defined date ranges (informational; the agenda is a
     * flat list so it doesn't render them as bands). Accepted so the
     * CalendarView's three view components share the same prop shape. */
    periods?: import('./calendar-config').PeriodDef[];
    colorBy: string;
    dateSource: CalendarDateSourceWithAll;
    onTaskClick?: (task: Task) => void;
    /** t18: right-click on a task row — parent opens the delete confirmation. */
    onDeleteTask?: (taskId: string) => void;
}

export function CalendarAgenda({
    anchor,
    tasks,
    customFields,
    sprints,
    sprintField,
    periods,
    colorBy,
    dateSource,
    onTaskClick,
    onDeleteTask,
}: CalendarAgendaProps) {
    const { t } = useI18n();

    const sorted = useMemo(() => {
        const now = anchor.getTime();
        return tasks
            .map((task) => {
                const resolution = resolveTaskDate(task, dateSource, sprints);
                if (!resolution.start) return null;
                // t18: derive day-key via extractIsoDate to avoid the
                // UTC→local TZ day shift (day-1 bug). For sort we still
                // want a numeric timestamp — convert via parseISO so the
                // day parses as local midnight (which matches the grid).
                const dayKey = extractIsoDate(resolution.start);
                if (!dayKey) return null;
                const ts = parseISO(dayKey).getTime();
                return { task, resolution, dayKey, ts };
            })
            .filter((x): x is NonNullable<typeof x> => x !== null)
            // Future + today first, then the past. Within each group, by
            // ascending date.
            .sort((a, b) => {
                const aFuture = a.ts >= now;
                const bFuture = b.ts >= now;
                if (aFuture !== bFuture) return aFuture ? -1 : 1;
                return a.ts - b.ts;
            });
    }, [tasks, sprints, sprintField, dateSource, anchor]);

    // t18: right-click context menu (task-row only — Agenda has no day
    // cells, so we don't show a "Create task at <date>" entry here).
    const [menuState, setMenuState] = useState<{
        open: boolean;
        x: number;
        y: number;
        task?: Task;
    }>({ open: false, x: 0, y: 0 });

    const closeMenu = useCallback(() => {
        setMenuState((s) => ({ ...s, open: false }));
    }, []);

    const openTaskMenu = useCallback((task: Task, ev: React.MouseEvent) => {
        ev.preventDefault();
        ev.stopPropagation();
        setMenuState({
            open: true,
            x: ev.clientX,
            y: ev.clientY,
            task,
        });
    }, []);

    const menuItems = useMemo<CalendarContextMenuItem[]>(() => {
        if (!menuState.open || !menuState.task) return [];
        const task = menuState.task;
        const items: CalendarContextMenuItem[] = [];
        if (onTaskClick) {
            items.push({
                id: 'open-task',
                label: t('calendar.contextMenu.openTask'),
                icon: 'open',
                onClick: () => onTaskClick(task),
            });
        }
        if (onDeleteTask) {
            items.push({
                id: 'delete-task',
                label: t('calendar.contextMenu.deleteTask'),
                icon: 'delete',
                danger: true,
                onClick: () => onDeleteTask(task.id),
            });
        }
        return items;
    }, [menuState, onTaskClick, onDeleteTask, t]);

    if (sorted.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-16 text-center rounded-lg border border-dashed border-white/10 bg-white/5">
                <CalendarIcon className="h-8 w-8 text-foreground-muted mb-3" />
                <h3 className="text-base font-medium text-foreground mb-1">
                    {t('calendar.agendaEmptyTitle')}
                </h3>
                <p className="text-sm text-foreground-muted max-w-md">
                    {t('calendar.agendaEmptyBody')}
                </p>
                <CalendarContextMenu
                    open={menuState.open}
                    x={menuState.x}
                    y={menuState.y}
                    items={menuItems}
                    onClose={closeMenu}
                />
            </div>
        );
    }

    // Group by yyyy-mm-dd so the list shows a "Tuesday 14" header for
    // each day. Done with a single pass + Map for stable order.
    const groups = new Map<string, Array<typeof sorted[number]>>();
    for (const entry of sorted) {
        const arr = groups.get(entry.dayKey) ?? [];
        arr.push(entry);
        groups.set(entry.dayKey, arr);
    }

    return (
        <div className="rounded-lg border border-white/10 bg-surface/30 divide-y divide-white/10">
            {Array.from(groups.entries()).map(([dayKey, entries]) => {
                const firstStart = entries[0]?.resolution.start;
                if (!firstStart) return null;
                // t18: parse the dayKey (YYYY-MM-DD) as local midnight
                // so the friendly label (Today/Tomorrow/EEEE) matches
                // the user's local TZ — no UTC shift.
                const dayDate = parseISO(dayKey);
                const label = isToday(dayDate)
                    ? t('common.today')
                    : isTomorrow(dayDate)
                        ? t('common.tomorrow')
                        : isThisWeek(dayDate, { weekStartsOn: 1 })
                            ? format(dayDate, 'EEEE')
                            : format(dayDate, 'EEEE, MMMM d');
                return (
                    <div key={dayKey} className="px-4 py-3">
                        <div className="flex items-baseline gap-3 mb-2">
                            <span className="text-sm font-semibold text-foreground">{label}</span>
                            <span className="text-xs text-foreground-muted">
                                {format(dayDate, 'MMM d, yyyy')}
                            </span>
                            <span className="text-xs text-foreground-muted/80 ml-auto">
                                {entries.length}
                            </span>
                        </div>
                        <div className="space-y-1.5">
                            {entries.map(({ task, resolution }) => {
                                const colorHex = colorToHex(
                                    resolveCalendarColor(task, colorBy, sprints, sprintField),
                                    customFields
                                );
                                const startIso = resolution.start ?? '';
                                // Detect a non-midnight time-of-day only
                                // when the backend actually sent a full
                                // ISO datetime with a time component.
                                const hasTime = startIso.includes('T') && startIso.length > 11;
                                const startLabel = hasTime ? format(new Date(startIso), 'HH:mm') : '';
                                return (
                                    <button
                                        key={task.id}
                                        type="button"
                                        onClick={() => onTaskClick?.(task)}
                                        onContextMenu={(e) => openTaskMenu(task, e)}
                                        className={cn(
                                            'group w-full flex items-center gap-3 rounded-md border border-white/10',
                                            'bg-surface-hover/20 hover:bg-surface-hover/50 transition-colors px-3 py-2 text-left'
                                        )}
                                    >
                                        <span
                                            className="inline-block h-8 w-1 rounded shrink-0"
                                            style={{ backgroundColor: colorHex }}
                                            aria-hidden="true"
                                        />
                                        {hasTime ? (
                                            <span className="text-xs tabular-nums text-foreground-muted shrink-0 w-12">
                                                {startLabel}
                                            </span>
                                        ) : (
                                            <span className="text-xs text-foreground-muted shrink-0 w-12">
                                                All day
                                            </span>
                                        )}
                                        <span className="text-sm text-foreground truncate flex-1">
                                            {task.title}
                                        </span>
                                        {task.assignees.length > 0 && (
                                            <span className="text-[11px] text-foreground-muted shrink-0">
                                                {task.assignees.length} {t('tasks.assignees').toLowerCase()}
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                );
            })}

            <CalendarContextMenu
                open={menuState.open}
                x={menuState.x}
                y={menuState.y}
                items={menuItems}
                onClose={closeMenu}
            />
        </div>
    );
}
